import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiSearch, FiChevronDown, FiX, FiMoreVertical, FiBarChart2 } from 'react-icons/fi';
import { api } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/ui/Toast';
import { resolveDisplayName, useUsersDirectory } from '../../lib/usersDirectory';
import ApplicationsSectionTabs from './ApplicationsSectionTabs';

const CARRIERS = [
  'F&G', 'Ethos', 'Ameritas', 'Corebridge', 'Foresters',
  'Mutual of Omaha', 'Quility', 'Banner', 'North American',
  'American National', 'Allianz', 'American Equity', 'Other',
];

const STATUSES = [
  { value: 'application_started', label: 'Application Started', color: 'bg-gray-500' },
  { value: 'submitted', label: 'Submitted', color: 'bg-blue-500' },
  { value: 'pending_requirements', label: 'Pending Requirements', color: 'bg-yellow-500' },
  { value: 'underwriting', label: 'Underwriting', color: 'bg-purple-500' },
  { value: 'approved', label: 'Approved', color: 'bg-green-500' },
  { value: 'advanced_paid', label: 'Advanced Paid', color: 'bg-emerald-600' },
  { value: 'declined', label: 'Declined', color: 'bg-red-500' },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-gray-400' },
];

function SortableHeader({ label, field, sortState, onSort }) {
  const active = sortState.field === field;
  return (
    <th
      onClick={() => onSort(field)}
      className="text-left px-4 py-3 text-xs font-medium text-gray-400 
                 uppercase tracking-wider cursor-pointer select-none 
                 hover:text-gray-200 transition-colors"
    >
      <div className="flex items-center gap-1">
        {label}
        <span className="text-gray-600">
          {active
            ? sortState.direction === 'asc' ? ' ↑' : ' ↓'
            : ' ↕'}
        </span>
      </div>
    </th>
  );
}

function StatusBadge({ status }) {
  const s = STATUSES.find(x => x.value === status);
  if (!s) return null;
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-white ${s.color}`}>
      {s.label}
    </span>
  );
}

function StatusDropdown({ currentStatus, onUpdate, disabled }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={e => { e.stopPropagation(); setOpen(o => !o); }}
        disabled={disabled}
        className="flex items-center gap-1 group"
      >
        <StatusBadge status={currentStatus} />
        <FiChevronDown className="w-3 h-3 text-gray-400 group-hover:text-white transition-colors" />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-48 bg-gray-800 border border-gray-700 rounded-lg shadow-xl overflow-hidden">
          {STATUSES.map(s => (
            <button
              key={s.value}
              onClick={e => {
                e.stopPropagation();
                onUpdate(s.value);
                setOpen(false);
              }}
              className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-700 transition-colors flex items-center gap-2 ${
                s.value === currentStatus ? 'bg-gray-700' : ''
              }`}
            >
              <span className={`w-2 h-2 rounded-full ${s.color}`} />
              <span className="text-gray-200">{s.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function getStoredFilters() {
  try {
    return JSON.parse(localStorage.getItem('prosperity_app_filters') || '{}');
  } catch {
    return {};
  }
}

const KANBAN_STATUSES = [
  { value: 'application_started', label: 'App Started', color: 'bg-gray-500' },
  { value: 'submitted', label: 'Submitted', color: 'bg-blue-500' },
  { value: 'pending_requirements', label: 'Pending Req.', color: 'bg-yellow-500' },
  { value: 'underwriting', label: 'Underwriting', color: 'bg-purple-500' },
  { value: 'approved', label: 'Approved', color: 'bg-green-500' },
  { value: 'advanced_paid', label: 'Advanced Paid', color: 'bg-emerald-600' },
  { value: 'declined', label: 'Declined', color: 'bg-red-500' },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-gray-400' },
];

function KanbanCard({ app, onStatusUpdate, navigate }) {
  const [menuOpen, setMenuOpen] = useState(false);

  const daysSince = (date) => {
    if (!date) return null;
    const diff = Date.now() - new Date(date).getTime();
    return Math.floor(diff / (1000 * 60 * 60 * 24));
  };

  const daysInStatus = daysSince(app.updated_at);
  const isStale = daysInStatus > 7;
  const agentName = resolveDisplayName(app.agent_id, app.agent, app.agent_name);

  const formatPoints = (pts) => {
    if (!pts) return null;
    return new Intl.NumberFormat('en-US').format(pts) + ' pts';
  };

  return (
    <div
      className="bg-gray-800 border border-gray-700 rounded-lg p-3 cursor-pointer
                 hover:border-gray-500 transition-colors group relative"
      onClick={() => navigate(`/applications/${app.id}`)}
    >
      {/* Card header */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <p className="text-sm font-medium text-white leading-tight flex-1">
          {app.client_name}
        </p>
        <button
          onClick={e => { e.stopPropagation(); setMenuOpen(o => !o); }}
          className="opacity-0 group-hover:opacity-100 text-gray-500 
                     hover:text-gray-300 transition-all flex-shrink-0"
        >
          <FiMoreVertical className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Carrier */}
      <p className="text-xs text-gray-400 mb-2">{app.carrier || '—'}</p>

      {/* Points / Premium */}
      <div className="flex items-center justify-between mb-2">
        {app.target_points ? (
          <span className="text-xs font-semibold text-[#00AAFF]">
            {formatPoints(app.target_points)}
          </span>
        ) : app.premium ? (
          <span className="text-xs text-gray-400">
            ${new Intl.NumberFormat('en-US').format(app.premium)}
          </span>
        ) : (
          <span className="text-xs text-gray-600">No points</span>
        )}
        {isStale && (
          <span className="text-xs text-yellow-500 font-medium">
            {daysInStatus}d
          </span>
        )}
      </div>

      {/* Agent */}
      {agentName && (
        <p className="text-xs text-gray-500 truncate">
          {agentName}
        </p>
      )}

      {/* Days in status indicator */}
      {daysInStatus !== null && !isStale && (
        <p className="text-xs text-gray-600 mt-1">{daysInStatus}d in status</p>
      )}

      {/* Quick status menu */}
      {menuOpen && (
        <div
          className="absolute right-0 top-8 w-44 bg-gray-800 border border-gray-700 
                     rounded-lg shadow-xl overflow-hidden z-50"
          onClick={e => e.stopPropagation()}
        >
          <p className="px-3 py-2 text-xs text-gray-500 border-b border-gray-700">
            Move to...
          </p>
          {KANBAN_STATUSES.map(s => (
            <button
              key={s.value}
              onClick={() => {
                onStatusUpdate(app.id, s.value);
                setMenuOpen(false);
              }}
              className="w-full text-left px-3 py-2 text-xs text-gray-300 
                         hover:bg-gray-700 flex items-center gap-2 transition-colors"
            >
              <span className={`w-2 h-2 rounded-full ${s.color}`} />
              {s.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function KanbanBoard({ applications, onStatusUpdate, navigate }) {
  const columns = KANBAN_STATUSES.map(status => ({
    ...status,
    cards: applications.filter(a => a.status === status.value),
    totalPoints: applications
      .filter(a => a.status === status.value)
      .reduce((sum, a) => sum + (parseFloat(a.target_points) || 0), 0),
  }));

  return (
    <div className="overflow-x-auto" style={{ cursor: 'grab' }}>
      <div className="flex gap-3 min-w-max pb-4">
        {columns.map(col => (
          <div key={col.value} className="w-56 flex-shrink-0">
            {/* Column header */}
            <div className="flex items-center justify-between mb-2 px-1">
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${col.color}`} />
                <span className="text-xs font-medium text-gray-300">
                  {col.label}
                </span>
                <span className="text-xs text-gray-600 bg-gray-800 
                                 px-1.5 py-0.5 rounded-full">
                  {col.cards.length}
                </span>
              </div>
            </div>

            {/* Column total points */}
            {col.totalPoints > 0 && (
              <p className="text-xs text-gray-600 px-1 mb-2">
                {new Intl.NumberFormat('en-US').format(col.totalPoints)} pts
              </p>
            )}

            {/* Cards */}
            <div className="space-y-2">
              {col.cards.length === 0 ? (
                <div className="border border-dashed border-gray-800 rounded-lg 
                               p-4 text-center">
                  <p className="text-xs text-gray-700">Empty</p>
                </div>
              ) : (
                col.cards.map(app => (
                  <KanbanCard
                    key={app.id}
                    app={app}
                    onStatusUpdate={onStatusUpdate}
                    navigate={navigate}
                  />
                ))
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ApplicationsList() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const showToast = (message, type = 'info') => addToast({ type, message });
  const { users: directoryUsers } = useUsersDirectory();

  const storedFilters = getStoredFilters();

  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(storedFilters.search || '');
  const [filterStatus, setFilterStatus] = useState(storedFilters.filterStatus || '');
  const [filterCarrier, setFilterCarrier] = useState(storedFilters.filterCarrier || '');
  const [filterAgent, setFilterAgent] = useState(storedFilters.filterAgent || '');
  const [updatingId, setUpdatingId] = useState(null);
  const [sortState, setSortState] = useState({ field: 'created_at', direction: 'desc' });
  const [view, setView] = useState(
    localStorage.getItem('prosperity_app_view') || 'list'
  );

  const isAdmin = ['admin', 'super_admin', 'manager'].includes(user?.role);
  const hasActiveFilters = search || filterStatus || filterCarrier || filterAgent;

  useEffect(() => {
    localStorage.setItem('prosperity_app_filters', JSON.stringify({
      search,
      filterStatus,
      filterCarrier,
      filterAgent,
    }));
  }, [search, filterStatus, filterCarrier, filterAgent]);

  const resetFilters = () => {
    setSearch('');
    setFilterStatus('');
    setFilterCarrier('');
    setFilterAgent('');
    localStorage.removeItem('prosperity_app_filters');
  };

  const handleSort = (field) => {
    setSortState(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc'
        ? 'desc' : 'asc',
    }));
  };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const filters = {};
      if (filterStatus) filters.status = filterStatus;
      if (filterCarrier) filters.carrier = filterCarrier;
      if (filterAgent) filters.agent_id = filterAgent;
      if (search) filters.search = search;
      const data = await api.applications.getAll(filters);
      setApplications(data);
    } catch (err) {
      showToast('Failed to load applications', 'error');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterCarrier, filterAgent, search]);

  useEffect(() => { load(); }, [load]);

  const agents = useMemo(() => {
    if (!isAdmin) return [];
    const agentSet = new Map();
    applications.forEach((app) => {
      if (app.agent_id) {
        const name = resolveDisplayName(app.agent_id, app.agent, app.agent_name) || 'Unknown';
        agentSet.set(app.agent_id, name);
      } else if (app.agent_name) {
        agentSet.set(app.agent_name, app.agent_name);
      }
    });
    return Array.from(agentSet.entries()).map(([id, name]) => ({ id, name }));
  }, [applications, isAdmin, directoryUsers]);

  const handleStatusUpdate = async (appId, newStatus) => {
    try {
      setUpdatingId(appId);
      await api.applications.updateStatus(appId, newStatus);
      setApplications(prev =>
        prev.map(a => a.id === appId ? { ...a, status: newStatus } : a)
      );
      showToast('Status updated', 'success');
    } catch (err) {
      showToast('Failed to update status', 'error');
    } finally {
      setUpdatingId(null);
    }
  };

  const formatCurrency = (val) => {
    if (!val) return '—';
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: 'USD', maximumFractionDigits: 0,
    }).format(val);
  };

  const formatPoints = (val) => {
    if (val == null) return null;
    return `${new Intl.NumberFormat('en-US').format(val)} pts`;
  };

  const formatDate = (val) => {
    if (!val) return '—';
    return new Date(val).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  };

  const sortedApplications = useMemo(() => {
    return [...applications].sort((a, b) => {
      const dir = sortState.direction === 'asc' ? 1 : -1;
      const aVal = a[sortState.field];
      const bVal = b[sortState.field];
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      if (typeof aVal === 'string') return aVal.localeCompare(bVal) * dir;
      return (aVal - bVal) * dir;
    });
  }, [applications, sortState]);

  const stats = STATUSES.map(s => ({
    ...s,
    count: applications.filter(a => a.status === s.value).length,
  })).filter(s => s.count > 0);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Applications</h1>
          <p className="text-gray-400 text-sm mt-1">
            {applications.length} application{applications.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap justify-end">
          <ApplicationsSectionTabs />
          <div className="flex items-center gap-1 bg-gray-800 rounded-lg p-1">
            <button
              onClick={() => { setView('list'); localStorage.setItem('prosperity_app_view', 'list'); }}
              className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                view === 'list'
                  ? 'bg-gray-700 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              List
            </button>
            <button
              onClick={() => { setView('kanban'); localStorage.setItem('prosperity_app_view', 'kanban'); }}
              className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                view === 'kanban'
                  ? 'bg-gray-700 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Kanban
            </button>
          </div>
          <button
            onClick={() => navigate('/applications/reports')}
            className="flex items-center gap-2 px-4 py-2 border border-gray-700 
                       hover:border-gray-500 text-gray-300 hover:text-white 
                       text-sm font-medium rounded-lg transition-colors"
          >
            <FiBarChart2 className="w-4 h-4" />
            Reports
          </button>
          <button
            onClick={() => navigate('/applications/new')}
            className="flex items-center gap-2 px-4 py-2 bg-[#00AAFF] hover:bg-blue-500 
                       text-white text-sm font-medium rounded-lg transition-colors"
          >
            <FiPlus className="w-4 h-4" />
            New Application
          </button>
        </div>
      </div>

      {/* Status Summary Pills */}
      {view === 'list' && stats.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setFilterStatus('')}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
              filterStatus === ''
                ? 'bg-[#00AAFF] text-white'
                : 'bg-gray-800 text-gray-400 hover:text-white'
            }`}
          >
            All ({applications.length})
          </button>
          {stats.map(s => (
            <button
              key={s.value}
              onClick={() => setFilterStatus(s.value === filterStatus ? '' : s.value)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                filterStatus === s.value
                  ? 'bg-[#00AAFF] text-white'
                  : 'bg-gray-800 text-gray-400 hover:text-white'
              }`}
            >
              {s.label} ({s.count})
            </button>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search by client name..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg 
                       text-sm text-white placeholder-gray-500 focus:outline-none 
                       focus:border-[#00AAFF] transition-colors"
          />
        </div>
        <select
          value={filterCarrier}
          onChange={e => setFilterCarrier(e.target.value)}
          className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm 
                     text-gray-300 focus:outline-none focus:border-[#00AAFF] transition-colors"
        >
          <option value="">All Carriers</option>
          {CARRIERS.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        {isAdmin && agents.length > 0 && (
          <select
            value={filterAgent}
            onChange={e => setFilterAgent(e.target.value)}
            className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm 
                       text-gray-300 focus:outline-none focus:border-[#00AAFF] transition-colors"
          >
            <option value="">All Agents</option>
            {agents.map(a => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
        )}
        {hasActiveFilters && (
          <button
            onClick={resetFilters}
            className="px-3 py-2 text-xs text-gray-400 hover:text-white 
                       border border-gray-700 rounded-lg transition-colors 
                       flex items-center gap-1.5"
          >
            <FiX className="w-3 h-3" /> Reset
          </button>
        )}
      </div>

      {/* Table / Kanban */}
      {view === 'kanban' ? (
        <div className="overflow-hidden rounded-xl border border-gray-800"
             style={{ height: 'calc(100vh - 280px)' }}>
          <div className="overflow-x-auto overflow-y-auto h-full p-4">
            <KanbanBoard
              applications={sortedApplications}
              onStatusUpdate={handleStatusUpdate}
              navigate={navigate}
            />
          </div>
        </div>
      ) : (
        <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-6 h-6 border-2 border-[#00AAFF] border-t-transparent 
                             rounded-full animate-spin" />
            </div>
          ) : applications.length === 0 ? (
            <div className="text-center py-20 text-gray-500">
              <p className="text-lg font-medium">No applications found</p>
              <p className="text-sm mt-1">Try adjusting your filters</p>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-800">
                  <SortableHeader
                    label="Client"
                    field="client_name"
                    sortState={sortState}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="Carrier / Product"
                    field="carrier"
                    sortState={sortState}
                    onSort={handleSort}
                  />
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Policy #
                  </th>
                  <SortableHeader
                    label="Points"
                    field="target_points"
                    sortState={sortState}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="Date Submitted"
                    field="submitted_at"
                    sortState={sortState}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="Status"
                    field="status"
                    sortState={sortState}
                    onSort={handleSort}
                  />
                  {isAdmin && (
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Agent
                    </th>
                  )}
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Latest Note
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {sortedApplications.map(app => (
                  <tr
                    key={app.id}
                    onClick={() => navigate(`/applications/${app.id}`)}
                    className="hover:bg-gray-800/50 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-white">{app.client_name}</div>
                      {app.client_phone && (
                        <div className="text-xs text-gray-500">{app.client_phone}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-200">{app.carrier || '—'}</div>
                      {app.product && (
                        <div className="text-xs text-gray-500">{app.product}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-300 font-mono">
                        {app.policy_number || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {app.target_points != null ? (
                        <>
                          <div className="text-sm text-gray-200">
                            {formatPoints(app.target_points)}
                          </div>
                          {app.premium && (
                            <div className="text-xs text-gray-500">
                              {formatCurrency(app.premium)}
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="text-sm text-gray-200">
                          {formatCurrency(app.premium)}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-300">
                        {formatDate(app.submitted_at)}
                      </span>
                    </td>
                    <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                      <StatusDropdown
                        currentStatus={app.status}
                        onUpdate={(newStatus) => handleStatusUpdate(app.id, newStatus)}
                        disabled={updatingId === app.id}
                      />
                    </td>
                    {isAdmin && (
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-300">
                          {resolveDisplayName(app.agent_id, app.agent, app.agent_name) || '—'}
                        </span>
                      </td>
                    )}
                    <td className="px-4 py-3 max-w-[200px]">
                      {app.latest_comment ? (
                        <div>
                          <p className="text-xs text-gray-400 truncate">
                            {app.latest_comment.body}
                          </p>
                          <p className="text-xs text-gray-600 mt-0.5">
                            {app.latest_comment.author_name} · {formatDate(app.latest_comment.created_at)}
                          </p>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-600">No notes</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
